import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { CloudOff, Loader2, RefreshCw, TimerReset, Wrench } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { checkNow, dismissWebUpdate, phaseOf, type UpdatePhase, useUpdateState } from '../lib/updates';
import { Button } from '../ui/button';
import { toast } from '../ui/feedback';

const timeFmt = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const dateTimeFmt = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/** Время для подписи: по часам сервера, показ — по часам человека; в другой день — с датой */
function at(ms: number, offset: number, now: number): string {
  const d = new Date(ms - offset);
  const today = new Date(now - offset);
  return d.toDateString() === today.toDateString() ? timeFmt.format(d) : dateTimeFmt.format(d);
}

function countdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function inMinutes(ms: number): string {
  const m = Math.max(1, Math.round(ms / 60_000));
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} ч ${m % 60} мин` : `${h} ч`;
}

/** Предупреждения показываются один раз за открытие страницы */
const toasted = new Set<string>();

/**
 * Плашка об обновлении портала (АД-09): за 5 минут — отсчёт, во время выкладки — «Идёт обновление», без
 * связи — автоповтор, после — «Вышла новая версия». В шапке портала и на страницах входа.
 */
export function UpdateBanner({ variant = 'app' }: { variant?: 'app' | 'auth' }) {
  const s = useUpdateState();
  // Время берётся при каждой отрисовке: её вызывают ответ API и, во время отсчёта, тик раз в секунду
  const [, setTick] = useState(0);
  const serverNow = Date.now() + s.offsetMs;
  const phase = phaseOf(s, serverNow);
  const ticking = phase.kind === 'scheduled' || phase.kind === 'maintenance';

  useEffect(() => {
    if (!ticking) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [ticking]);

  // Всплывающее предупреждение видно и поверх открытого окна формы: при объявлении и за минуту до начала
  useEffect(() => {
    if (phase.kind !== 'scheduled') return;
    const left = phase.startsAt - serverNow;
    const id = phase.release.id;
    if (!toasted.has(id)) {
      toasted.add(id);
      if (left <= 60_000) toasted.add(`${id}:1`);
      toast(`Через ${inMinutes(left)} портал обновится — сохраните изменения`, 'info');
    }
    if (left <= 60_000 && !toasted.has(`${id}:1`)) {
      toasted.add(`${id}:1`);
      toast('Через минуту портал обновится — сохраните изменения', 'info');
    }
  }, [phase, serverNow]);

  const view = phase.kind === 'none' ? null : describe(phase, serverNow, s.offsetMs);
  return (
    <>
      {/* Постоянная область для экранного диктора: объявляет смену состояния, а не каждую секунду отсчёта */}
      <p className="sr-only" role="status" aria-live="polite">
        {view?.spoken ?? ''}
      </p>
      {view ? <Bar view={view} variant={variant} /> : null}
    </>
  );
}

interface View {
  tone: 'warning' | 'danger' | 'dark';
  icon: ReactNode;
  title: ReactNode;
  text: string | null;
  spoken: string;
  action: 'retry' | 'reload' | null;
  commit?: string;
}

function describe(phase: Exclude<UpdatePhase, { kind: 'none' }>, serverNow: number, offset: number): View {
  switch (phase.kind) {
    case 'scheduled': {
      const left = phase.startsAt - serverNow;
      return {
        tone: 'warning',
        icon: <TimerReset className="size-5" aria-hidden />,
        title: (
          <>
            Через{' '}
            <span className="num font-semibold" aria-hidden>
              {countdown(left)}
            </span>{' '}
            портал обновится
          </>
        ),
        text: 'Сохраните то, что вводите: во время обновления портал может ненадолго не отвечать.',
        spoken: `В ${at(phase.startsAt, offset, serverNow)} портал обновится. Сохраните то, что вводите.`,
        action: null,
      };
    }
    case 'pending':
      return {
        tone: 'warning',
        icon: <Loader2 className="size-5 animate-spin motion-reduce:animate-none" aria-hidden />,
        title: 'Обновление начнётся в ближайшие минуты',
        text: 'Новая версия собирается. Портал работает, но может на минуту пропасть — введённое на странице не пропадёт.',
        spoken: 'Обновление портала начнётся в ближайшие минуты.',
        action: null,
      };
    case 'updating':
      return {
        tone: 'warning',
        icon: <Loader2 className="size-5 animate-spin motion-reduce:animate-none" aria-hidden />,
        title: 'Идёт обновление портала',
        text: 'Если страница или сохранение не открываются — подождите минуту и повторите. Введённое на странице не пропадёт.',
        spoken: 'Идёт обновление портала.',
        action: null,
      };
    case 'unreachable':
      return {
        tone: 'danger',
        icon: <CloudOff className="size-5" aria-hidden />,
        title: phase.duringRelease ? 'Идёт обновление — портал пока не отвечает' : 'Нет связи с порталом',
        text: phase.duringRelease
          ? 'Связь проверяется каждые 10 секунд. Не закрывайте страницу — введённое на ней не пропадёт.'
          : 'Проверьте интернет. Связь проверяется каждые 10 секунд; введённое на странице не пропадёт.',
        spoken: phase.duringRelease ? 'Идёт обновление, портал пока не отвечает.' : 'Нет связи с порталом.',
        action: 'retry',
      };
    case 'updated':
      return {
        tone: 'dark',
        icon: <RefreshCw className="size-5" aria-hidden />,
        title: 'Вышла новая версия портала',
        text: 'Сохраните изменения и обновите страницу.',
        spoken: 'Вышла новая версия портала. Сохраните изменения и обновите страницу.',
        action: 'reload',
        commit: phase.commit,
      };
    case 'maintenance': {
      const until = at(phase.endsAt, offset, serverNow);
      const what = phase.message ? `: ${phase.message}` : '';
      const by = phase.by ? ` Автор предупреждения: ${phase.by}.` : '';
      if (serverNow < phase.startsAt) {
        return {
          tone: 'warning',
          icon: <Wrench className="size-5" aria-hidden />,
          title: `Через ${inMinutes(phase.startsAt - serverNow)} — работы на портале до ${until}${what}`,
          text: `Сохраните изменения заранее: во время работ портал может не открываться.${by}`,
          spoken: `В ${at(phase.startsAt, offset, serverNow)} начнутся работы на портале до ${until}${what}.`,
          action: null,
        };
      }
      return {
        tone: 'warning',
        icon: <Wrench className="size-5" aria-hidden />,
        title: `Идут работы на портале до ${until}${what}`,
        text: `Возможны перерывы. Введённое на странице не пропадёт — если сохранение не прошло, повторите позже.${by}`,
        spoken: `Идут работы на портале до ${until}${what}.`,
        action: null,
      };
    }
  }
}

function Bar({ view, variant }: { view: View; variant: 'app' | 'auth' }) {
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    setRetrying(true);
    await checkNow();
    setRetrying(false);
  };
  const { tone } = view;
  return (
    <div
      className={clsx(
        'border-b print:hidden',
        tone === 'warning' && 'border-warning/30 bg-warning-soft text-ink',
        tone === 'danger' && 'border-danger/30 bg-danger-soft text-ink',
        tone === 'dark' && 'border-ink bg-ink text-on-ink',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 md:px-8">
        <span
          className={clsx(
            'shrink-0',
            tone === 'warning' && 'text-warning',
            tone === 'danger' && 'text-danger',
            tone === 'dark' && 'text-accent',
          )}
        >
          {view.icon}
        </span>
        <div className="min-w-0 flex-1 basis-[220px] text-[13px] leading-5" aria-hidden>
          <span className="font-semibold">{view.title}</span>
          {view.text ? (
            <span className={clsx('ml-1.5', tone === 'dark' ? 'text-on-ink/80' : 'text-ink-2')}>
              {view.text}
            </span>
          ) : null}
        </div>
        {view.action === 'retry' ? (
          <Button size="sm" variant="secondary" loading={retrying} onClick={retry}>
            Проверить сейчас
          </Button>
        ) : null}
        {view.action === 'reload' ? (
          <div className="flex flex-wrap items-center gap-1">
            <Button size="sm" variant="primary" onClick={() => window.location.reload()}>
              Обновить страницу
            </Button>
            {variant === 'app' ? (
              <Link
                to="/whats-new"
                className="inline-flex h-11 items-center px-2 text-[13px] font-medium underline-offset-[3px] hover:underline md:h-8"
              >
                Что нового
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => view.commit && dismissWebUpdate(view.commit)}
              className="inline-flex h-11 items-center px-2 text-[13px] text-on-ink/75 hover:text-on-ink md:h-8"
            >
              Позже
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
